import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, NgModule, OnChanges, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { PaginationQueryParamsModule } from '@commons/pipes/buildQueryParams/build-query-params.pipe';
import { BreakpointsService } from '@core/services/breakpoints/breakpoints.service';
import { environment } from '@environment';
import { Page } from '@models/pagination/page';
import { RelEnum } from '@models/pagination/rel.enum';
import { SharedModule } from '@shared/shared.module';
import { trackEventWithAtInternet } from '@wizbii/tracking';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Component({
  selector: 'app-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginationComponent implements OnChanges {
  get isMobile$(): Observable<boolean> {
    return this.breakpointsService.isMobile$;
  }
  steps = 10;
  trackEvent = trackEventWithAtInternet;

  computedPagination$ = new BehaviorSubject<Page[]>([]);

  @Input() nbHits!: number;
  @Input() currentPageNumber!: number;
  @Input() trackerCategory!: string;
  @Input() itemsPerPage = environment.paging.item_per_page;
  @Output() paginationClicked = new EventEmitter<void>();

  vm$ = combineLatest([this.computedPagination$, this.isMobile$]).pipe(
    tap(([_, isMobile]) => (this.steps = this.definedSteps(isMobile))),
    map(([pagination, isMobile]) => ({
      pagination,
      isMobile,
    }))
  );

  constructor(private readonly breakpointsService: BreakpointsService) {}

  ngOnChanges(): void {
    const nbPages = this.getPages(this.nbHits);
    this.computedPagination$.next(this.getComputedPagination({ currentPageNumber: this.currentPageNumber, nbPages }));
  }

  private definedSteps(isMobile: boolean): number {
    return isMobile ? 5 : this.steps;
  }

  pageClicked(elementId: number): void {
    this.paginationClicked.emit();
    if (this.trackerCategory) {
      this.trackEvent({
        category: this.trackerCategory,
        label: elementId.toString(),
        action: 'Click Page',
        type: 'navigation',
      });
    }
  }

  trackByPage(_: number, page: Page): number {
    return page.number;
  }

  private getComputedPagination({
    currentPageNumber,
    nbPages,
  }: {
    currentPageNumber: number;
    nbPages: number;
  }): Page[] {
    return nbPages < 2
      ? []
      : [
          this.getFirstPage(currentPageNumber),
          ...this.getQuickNavigationBackward(currentPageNumber),
          ...this.getPreviousSteps(currentPageNumber),
          ...this.getCurrentPage(currentPageNumber, nbPages),
          ...this.getNextSteps(currentPageNumber, nbPages),
          ...this.getQuickNavigationForward(currentPageNumber, nbPages),
          this.getLastPage(nbPages),
        ];
  }

  private getQuickNavigationBackward(currentPageNumber: number): Page[] {
    const quickNavigationBackwardElements = [];
    const currentPageLowTenBoundary = Math.floor(currentPageNumber / this.steps) * this.steps;

    for (let i = currentPageLowTenBoundary - this.steps; i >= this.steps; i -= this.steps) {
      quickNavigationBackwardElements.unshift({
        number: i,
        rel: null,
      });
    }

    return quickNavigationBackwardElements;
  }

  private getQuickNavigationForward(currentPageNumber: number, elements: number): Page[] {
    const quickNavigationForwardElements = [];
    const currentPageMaxTenBoundary =
      Math.ceil((currentPageNumber % this.steps === 0 ? currentPageNumber + 1 : currentPageNumber) / this.steps) *
      this.steps;

    for (let i = currentPageMaxTenBoundary + this.steps; i < elements; i += this.steps) {
      quickNavigationForwardElements.push({
        number: i,
        rel: null,
      });
    }

    return quickNavigationForwardElements;
  }

  private getPreviousSteps(currentPageNumber: number): Page[] {
    const previousSteps: Page[] = [];
    const currentPageLowTenBoundary =
      currentPageNumber < this.steps ? 2 : Math.floor(currentPageNumber / this.steps) * this.steps;

    for (
      let i =
        currentPageLowTenBoundary === currentPageNumber && currentPageNumber >= this.steps
          ? currentPageLowTenBoundary - 1
          : currentPageLowTenBoundary;
      i < currentPageNumber;
      i++
    ) {
      previousSteps.push({
        number: i,
        rel: i === currentPageNumber - 1 ? RelEnum.REL_PREV : RelEnum.NO_REL,
      });
    }

    return previousSteps;
  }

  private getNextSteps(currentPageNumber: number, maxElements: number): Page[] {
    const nextSteps: Page[] = [];

    let currentPageMaxTenBoundary =
      Math.ceil((currentPageNumber % this.steps === 0 ? currentPageNumber + 1 : currentPageNumber) / this.steps) *
      this.steps;

    currentPageMaxTenBoundary = currentPageMaxTenBoundary >= maxElements ? maxElements - 1 : currentPageMaxTenBoundary;

    for (let i = currentPageNumber + 1; i <= currentPageMaxTenBoundary; i++) {
      nextSteps.push({
        number: i,
        rel: i === currentPageNumber + 1 ? RelEnum.REL_NEXT : RelEnum.NO_REL,
      });
    }

    return nextSteps;
  }

  private getPages(nbHits: number): number {
    const elements = nbHits > environment.paging.max_hits ? environment.paging.max_hits : nbHits;
    return Math.ceil(elements / this.itemsPerPage);
  }

  private getCurrentPage(currentPageNumber: number, lastElementNumber: number): Page[] {
    return currentPageNumber === 1 || currentPageNumber === lastElementNumber
      ? []
      : [{ number: currentPageNumber, rel: null }];
  }

  private getFirstPage(page: number): Page {
    return { number: page === 1 ? page : 1, rel: null };
  }

  private getLastPage(pages: number): Page {
    return { number: pages, rel: null };
  }
}

@NgModule({
  imports: [CommonModule, SharedModule, PaginationQueryParamsModule, MatButtonModule],
  declarations: [PaginationComponent],
  exports: [PaginationComponent],
})
export class PaginationModule {}
